/**
 * /bank-rec/$recId — bank reconciliation detail / matching workspace.
 *
 * Header: bank account, statement date, status, totals strip
 *   beg balance · ending balance · book balance · outstanding · diff
 * Toolbar:
 *   - Import statement CSV → opens BankStatementImportDialog
 *   - Auto-match → POST /api/bank-reconciliations/{id}/auto-match
 *   - Complete → POST .../complete  (only when balanced)
 * Statement-lines grid: date · description · reference · amount ·
 *   matched chip · per-row "match…" button.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DataGridPro,
  type GridColDef,
  type GridRenderCellParams,
  type GridRowSelectionModel,
} from "@mui/x-data-grid-pro";
import { Box, Chip } from "@mui/material";

import { api } from "@/lib/api";
import { codeAndName, labelOf } from "@/lib/displayCode";
import {
  accountsFullListQueryOptions,
  bankRecDetailQueryOptions,
  bankRecKeys,
  glKeys,
  trialBalanceKeys,
} from "@/lib/queries";
import { gridDefaults, makeGroupable } from "@/grids/gridDefaults";
import { BankStatementImportDialog } from "@/components/BankStatementImportDialog";
import { ExceptionCards } from "@/components/ExceptionCards";
import { RunSummaryCard } from "@/components/RunSummaryCard";
import { useConfirmAction } from "@/components/ConfirmDialog";
import { useToast } from "@/components/Toaster";
import type { BankReconciliationStatus, BankStatementLine } from "@/lib/types";

// One chip style per status: draft is neutral (nothing wrong with a fresh
// rec), in_progress is amber (work pending), completed green, cancelled grey.
const STATUS_CHIP: Record<
  BankReconciliationStatus,
  { color: "default" | "success" | "warning"; variant: "filled" | "outlined" }
> = {
  draft: { color: "default", variant: "outlined" },
  in_progress: { color: "warning", variant: "outlined" },
  completed: { color: "success", variant: "filled" },
  cancelled: { color: "default", variant: "filled" },
};

export const Route = createFileRoute("/_auth/bank-rec/$recId")({
  component: BankRecDetailPage,
});

type AiReconMatch = {
  statement_line_id: string;
  journal_line_id: string;
  confidence: "high" | "medium" | "low";
  reasoning: string;
  amount_cents: number;
  date_diff_days: number | null;
  statement_date: string;
  statement_description: string;
  statement_reference: string;
  journal_no: number;
  journal_date: string;
  journal_description: string;
};

type AiReconResult = {
  summary: string;
  matches_proposed: number;
  high_confidence_count: number;
  matches: AiReconMatch[];
  orphan_statement_lines: Array<{
    id: string; date: string; amount_cents: number;
    description: string; best_guess: string | null;
  }>;
  orphan_journal_lines: Array<{
    id: string; journal_no: number; date: string;
    amount_cents: number; description: string; explanation: string | null;
  }>;
  generated_at: string;
  model: string;
};

type LedgerCandidate = {
  journal_line_id: string;
  journal_id: string;
  journal_no: number;
  journal_date: string;
  journal_description: string | null;
  line_memo: string | null;
  account_id: string;
  account_code: string;
  account_name: string;
  source_amount: string;
  suggested_amount: string;
  allocated_amount: string;
  available_amount: string;
  suggested_role: MatchRole;
};

type MatchRole = "principal" | "settlement" | "fee" | "adjustment" | "refund" | "chargeback";

type MatchGroup = {
  id: string;
  status: "draft" | "balanced" | "reversed";
  match_type: string;
  statement_total: string;
  ledger_total: string;
  difference: string;
  settlement_reference: string | null;
  notes: string | null;
  matched_at: string;
  allocations: Array<{
    id: string;
    side: "statement" | "ledger" | "clearing";
    role: MatchRole;
    statement_line_id: string | null;
    journal_line_id: string | null;
    allocated_amount: string;
    source_amount: string;
  }>;
};

function fmtMoney(n: string | number | null | undefined): string {
  if (n == null || n === "") return "—";
  const v = typeof n === "string" ? Number(n) : n;
  if (!Number.isFinite(v)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", maximumFractionDigits: 2,
  }).format(v);
}

// Bank transaction dates are date-only accounting facts. Anchor them at
// local noon so the grid cannot render August 1 as July 31 west of UTC.
function parseAccountingDate(value: unknown): Date | null {
  return value ? new Date(`${String(value)}T12:00:00`) : null;
}

function BankRecDetailPage() {
  const { recId } = Route.useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const confirmAction = useConfirmAction();

  const [importOpen, setImportOpen] = useState(false);
  const [industrialMatchOpen, setIndustrialMatchOpen] = useState(false);
  const [selectedStatementIds, setSelectedStatementIds] = useState<Set<string>>(new Set());

  const recQuery = useQuery(bankRecDetailQueryOptions(recId));
  const accountsQuery = useQuery(accountsFullListQueryOptions({ active_only: false }));

  const account = useMemo(() => {
    if (!recQuery.data) return null;
    return (accountsQuery.data ?? []).find((a) => a.id === recQuery.data!.bank_account_id) ?? null;
  }, [recQuery.data, accountsQuery.data]);

  const autoMatch = useMutation({
    mutationFn: () =>
      api(`/api/bank-reconciliations/${recId}/auto-match`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bankRecKeys.detail(recId) });
      toast.success("Auto-match complete");
    },
    onError: (err: Error) => toast.error(`Auto-match failed: ${err.message}`),
  });

  const aiSuggest = useMutation({
    mutationFn: () =>
      api<AiReconResult>(`/api/bank-reconciliations/${recId}/ai-suggest`, { method: "POST" }),
    onError: (err: Error) => toast.error(`AI suggest failed: ${err.message}`),
  });
  const [aiOpen, setAiOpen] = useState(false);

  const complete = useMutation({
    mutationFn: (allowImbalance: boolean) =>
      api(`/api/bank-reconciliations/${recId}/complete`, {
        method: "POST",
        body: { allow_imbalance: allowImbalance },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bankRecKeys.all });
      qc.invalidateQueries({ queryKey: glKeys.all });
      qc.invalidateQueries({ queryKey: trialBalanceKeys.all });
      toast.success("Reconciliation completed");
    },
    onError: (err: Error) => toast.error(`Complete failed: ${err.message}`),
  });

  const requestComplete = (allowImbalance: boolean) => {
    confirmAction({
      title: "Complete this reconciliation?",
      message:
        allowImbalance
          ? "Forcing completion despite imbalance — book balance ≠ statement ending balance.\n\n" +
            "This also overrides wholly unmatched statement rows, but not partial allocations or draft match groups. The continuous-close control center will keep reporting unresolved rows as blockers. Do this only after controller review."
          : "All matched and balanced. Posting completion record.",
      confirmLabel: "Complete",
      kind: allowImbalance ? "danger" : "primary",
      onConfirm: async () => { await complete.mutateAsync(allowImbalance); },
    });
  };

  if (recQuery.isLoading) {
    return <div className="p-8 text-sm text-slate-500 dark:text-slate-400">Loading…</div>;
  }
  if (recQuery.error) {
    return <div className="p-8 text-sm text-red-600">Error: {(recQuery.error as Error).message}</div>;
  }
  const r = recQuery.data;
  if (!r) return null;

  const beg = Number(r.statement_beginning_balance);
  const end = Number(r.statement_ending_balance);
  const book = r.book_balance != null ? Number(r.book_balance) : null;
  const outstanding = r.outstanding_total != null ? Number(r.outstanding_total) : null;
  // Reconciled book = book + outstanding (deposits in transit, outstanding cks)
  const reconciledBook = book != null ? book + (outstanding ?? 0) : null;
  const diff = reconciledBook != null ? reconciledBook - end : null;
  const balanced = diff != null && Math.abs(diff) < 0.005;

  const matchedCount = r.statement_lines.filter((l) => l.is_matched).length;
  // Toolbar is available for any non-terminal rec. A brand-new rec is
  // "draft"; previously the toolbar only rendered for "in_progress", so a
  // freshly created reconciliation had no way to import a statement or match.
  const canEdit = r.status !== "completed" && r.status !== "cancelled";

  const columns: GridColDef<BankStatementLine>[] = [
    {
      field: "txn_date",
      headerName: "Date",
      width: 110,
      type: "date",
      valueGetter: parseAccountingDate,
    },
    { field: "description", headerName: "Description", flex: 2, minWidth: 260 },
    {
      field: "reference",
      headerName: "Reference",
      width: 140,
      renderCell: (p) =>
        p.value ? (
          <Box component="span" sx={{ fontFamily: "monospace", fontSize: 12 }}>
            {p.value as string}
          </Box>
        ) : (
          <Box sx={{ color: "text.disabled" }}>—</Box>
        ),
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 130,
      type: "number",
      valueGetter: (v) => (v ? Number(v) : 0),
      valueFormatter: (v) => fmtMoney(v as number),
      cellClassName: "rec-amount",
    },
    {
      field: "is_matched",
      headerName: "Matched",
      width: 110,
      type: "boolean",
      renderCell: (p: GridRenderCellParams<BankStatementLine>) =>
        p.value ? (
          <Chip
            size="small"
            label="matched"
            color="success"
            variant="filled"
            sx={{ fontSize: 10, height: 20 }}
          />
        ) : (
          <Chip
            size="small"
            label="unmatched"
            color="default"
            variant="outlined"
            sx={{ fontSize: 10, height: 20 }}
          />
        ),
    },
    {
      field: "matched_journal_line_id",
      headerName: "Journal line",
      width: 200,
      renderCell: (p) =>
        p.value ? (
          <Box component="span" sx={{ fontFamily: "monospace", fontSize: 11, color: "text.secondary" }}>
            {(p.value as string).slice(0, 8)}…
          </Box>
        ) : (
          <Box sx={{ color: "text.disabled" }}>—</Box>
        ),
    },
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="text-xs text-slate-500 mb-1 dark:text-slate-400">
          <Link to="/bank-rec" className="hover:text-slate-700">Bank Reconciliation</Link>
          <span className="mx-2">/</span>
          <span className="font-medium text-slate-700 font-mono dark:text-slate-300">
            {account ? codeAndName(account) : r.bank_account_id.slice(0, 8) + "…"}
          </span>
          <span className="mx-2">·</span>
          <span>{r.statement_date}</span>
        </div>
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            {account?.name ?? "Bank reconciliation"}
            <span className="ml-2 text-sm font-normal text-slate-500 dark:text-slate-400">
              · statement {r.statement_date}
            </span>
          </h1>
          <Chip
            size="small"
            label={r.status}
            color={(STATUS_CHIP[r.status] ?? STATUS_CHIP.in_progress).color}
            variant={(STATUS_CHIP[r.status] ?? STATUS_CHIP.in_progress).variant}
          />
          <div className="flex-1" />
          {canEdit && (
            <>
              <button
                type="button"
                onClick={() => setImportOpen(true)}
                className="text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md px-3 py-1.5 font-medium dark:bg-gray-800 dark:text-slate-300"
              >
                📥 Import statement
              </button>
              <button
                type="button"
                onClick={() => autoMatch.mutate()}
                disabled={autoMatch.isPending || r.statement_lines.length === 0}
                className="text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md px-3 py-1.5 font-medium disabled:opacity-50 dark:bg-gray-800 dark:text-slate-300"
                title="Match statement lines to journal lines by date + amount + reference"
              >
                {autoMatch.isPending ? "Matching…" : "🔗 Auto-match"}
              </button>
              <button
                type="button"
                onClick={() => setIndustrialMatchOpen(true)}
                disabled={selectedStatementIds.size === 0}
                className="text-sm bg-violet-100 hover:bg-violet-200 text-violet-900 rounded-md px-3 py-1.5 font-medium disabled:opacity-50 dark:bg-violet-900/25 dark:text-violet-200"
                title="Select one or more unmatched statement rows, then allocate partials, splits, settlements, and fees"
              >
                ⑂ Build match ({selectedStatementIds.size})
              </button>
              <button
                type="button"
                onClick={() => setIndustrialMatchOpen(true)}
                className="text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md px-3 py-1.5 font-medium dark:bg-gray-800 dark:text-slate-300"
                title="Review balanced, draft, and reversed match groups"
              >
                Match history
              </button>
              <button
                type="button"
                onClick={() => {
                  setAiOpen(true);
                  if (!aiSuggest.data) aiSuggest.mutate();
                }}
                disabled={aiSuggest.isPending || r.statement_lines.length === 0}
                className="text-sm bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-md px-3 py-1.5 font-medium disabled:opacity-50 dark:bg-blue-900/20 dark:text-blue-300 dark:hover:bg-blue-900/40"
                title="AI proposes matches between unmatched bank lines and unmatched JEs"
              >
                {aiSuggest.isPending ? "Thinking…" : "✨ AI suggest"}
              </button>
              <button
                type="button"
                onClick={() => requestComplete(!balanced)}
                disabled={complete.isPending}
                className={`text-sm rounded-md px-3 py-1.5 font-medium ${
                  balanced
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                    : "bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300"
                }`}
                title={balanced ? "Mark this reconciliation completed" : "Force completion despite imbalance"}
              >
                {complete.isPending ? "Completing…" : balanced ? "✓ Complete" : "⚠ Force complete"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Totals strip */}
      <div
        className={`border-b px-6 py-3 flex items-center gap-6 text-sm ${
          balanced
            ? "bg-emerald-50 border-emerald-200"
            : r.status === "completed"
            ? "bg-slate-50 border-slate-200"
            : "bg-amber-50 border-amber-200"
        }`}
      >
        <Stat label="Beg. balance" value={fmtMoney(beg)} />
        <Stat label="End. balance" value={fmtMoney(end)} />
        <Stat label="Book balance" value={fmtMoney(book)} />
        <Stat label="Outstanding" value={fmtMoney(outstanding)} />
        {reconciledBook != null && (
          <Stat
            label="Reconciled book"
            value={fmtMoney(reconciledBook)}
            highlight
          />
        )}
        {diff != null && (
          <Stat
            label="Difference"
            value={fmtMoney(diff)}
            tone={balanced ? "ok" : "warn"}
          />
        )}
        <div className="flex-1" />
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {matchedCount} / {r.statement_lines.length} statement lines matched
        </span>
      </div>

      {/* Automated-run brief (present when the orchestrator seeded this rec) */}
      {r.run_summary && <RunSummaryCard summary={r.run_summary} />}

      {/* Investigator exception queue (renders only when exceptions exist) */}
      <ExceptionCards recId={recId} />

      {/* Grid */}
      <div className="flex-1 px-6 py-4 overflow-hidden">
        {r.statement_lines.length === 0 ? (
          <div className="text-sm text-slate-400 italic dark:text-slate-500">
            No statement lines yet. Click <strong>Import statement</strong> to upload a PDF, CSV, or Excel bank statement.
          </div>
        ) : (
          <Box sx={{ height: "100%", width: "100%" }}>
            <DataGridPro
              {...gridDefaults}
              rows={r.statement_lines}
              columns={makeGroupable(columns)}
              getRowId={(row) => row.id}
              checkboxSelection={canEdit}
              rowSelectionModel={{ type: "include", ids: selectedStatementIds } as GridRowSelectionModel}
              disableRowSelectionOnClick
              isRowSelectable={(params) => canEdit && !params.row.is_matched}
              onRowSelectionModelChange={(model) => {
                // MUI X v9 uses {type, ids}; retain compatibility with older arrays.
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const value = model as any;
                if (value?.ids instanceof Set) {
                  const ids = value.type === "exclude"
                    ? new Set(r.statement_lines.filter((line) => !line.is_matched).map((line) => line.id).filter((id) => !value.ids.has(id)))
                    : new Set(Array.from(value.ids, String));
                  setSelectedStatementIds(ids);
                } else {
                  setSelectedStatementIds(new Set(Array.isArray(value) ? value.map(String) : []));
                }
              }}
              density="compact"
              pinnedColumns={{ left: ["txn_date", "description"] }}
              sx={{
                bgcolor: "background.paper",
                border: 1, borderColor: "divider", borderRadius: 2,
                "& .MuiDataGrid-columnHeaders": {
                  bgcolor: "action.hover", fontSize: 11, fontWeight: 600,
                  textTransform: "uppercase", letterSpacing: "0.04em", color: "text.secondary",
                },
                "& .rec-amount": { fontFamily: "monospace", fontWeight: 600 },
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
              }}
            />
          </Box>
        )}
      </div>

      <BankStatementImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        recId={recId}
      />
      <AiReconDialog
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        data={aiSuggest.data ?? null}
        isPending={aiSuggest.isPending}
        error={aiSuggest.error ? (aiSuggest.error as Error).message : null}
        onRefresh={() => aiSuggest.mutate()}
        recId={recId}
        onApplied={() => {
          qc.invalidateQueries({ queryKey: bankRecKeys.detail(recId) });
          aiSuggest.mutate();  // re-run to see what's left
        }}
        toast={toast}
      />
      <IndustrialMatchDialog
        open={industrialMatchOpen}
        onClose={() => setIndustrialMatchOpen(false)}
        recId={recId}
        statementLines={r.statement_lines.filter((line) => selectedStatementIds.has(line.id))}
        onSaved={(group) => {
          qc.invalidateQueries({ queryKey: bankRecKeys.detail(recId) });
          qc.invalidateQueries({ queryKey: ["bank-reconciliation", recId, "match-groups"] });
          qc.invalidateQueries({ queryKey: ["bank-reconciliation", recId, "match-candidates"] });
          setSelectedStatementIds(new Set());
          if (group.status === "balanced") toast.success(`${group.match_type.replaceAll("_", " ")} match balanced`);
          else toast.error(`Match saved as draft — difference ${fmtMoney(group.difference)}`);
          setIndustrialMatchOpen(false);
        }}
        toast={toast}
        confirmAction={confirmAction}
      />
    </div>
  );
}

function Stat({
  label, value, tone = "neutral", highlight,
}: { label: string; value: string; tone?: "ok" | "warn" | "neutral"; highlight?: boolean }) {
  const cls =
    tone === "warn" ? "text-amber-800"
      : tone === "ok" ? "text-emerald-800"
      : "text-slate-900";
  return (
    <div>
      <span className="text-xs uppercase tracking-wide text-slate-500 mr-2 dark:text-slate-400">{label}</span>
      <span className={`font-mono ${highlight ? "font-bold" : "font-semibold"} ${cls}`}>
        {value}
      </span>
    </div>
  );
}

// ─── Industrial partial / split / settlement matcher ────────────────────

function IndustrialMatchDialog({
  open,
  onClose,
  recId,
  statementLines,
  onSaved,
  toast,
  confirmAction,
}: {
  open: boolean;
  onClose: () => void;
  recId: string;
  statementLines: BankStatementLine[];
  onSaved: (group: MatchGroup) => void;
  toast: ReturnType<typeof useToast>;
  confirmAction: ReturnType<typeof useConfirmAction>;
}) {
  const qc = useQueryClient();
  const statementKey = statementLines.map((line) => line.id).sort().join(",");
  const [statementDraft, setStatementDraft] = useState<Record<string, { amount: string; role: MatchRole }>>({});
  const [ledgerDraft, setLedgerDraft] = useState<Record<string, { selected: boolean; amount: string; role: MatchRole }>>({});
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [search, setSearch] = useState("");
  const [reverseReasons, setReverseReasons] = useState<Record<string, string>>({});

  const candidates = useQuery({
    queryKey: ["bank-reconciliation", recId, "match-candidates"],
    queryFn: () => api<LedgerCandidate[]>(`/api/bank-reconciliations/${recId}/match-candidates`),
    enabled: open,
  });
  const groups = useQuery({
    queryKey: ["bank-reconciliation", recId, "match-groups"],
    queryFn: () => api<MatchGroup[]>(`/api/bank-reconciliations/${recId}/match-groups`),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    const activeGroups = (groups.data ?? []).filter((group) => group.status !== "reversed");
    setStatementDraft(Object.fromEntries(statementLines.map((line) => {
      const used = activeGroups.reduce((total, group) => total + group.allocations
        .filter((allocation) => allocation.statement_line_id === line.id)
        .reduce((groupTotal, allocation) => groupTotal + Math.abs(Number(allocation.allocated_amount)), 0), 0);
      const remaining = Math.max(0, Math.abs(Number(line.amount)) - used);
      return [
        line.id,
        { amount: String(Number(line.amount) < 0 ? -remaining : remaining), role: "principal" as MatchRole },
      ];
    })));
    setLedgerDraft({});
    setReference("");
    setNotes("");
    setSearch("");
    setReverseReasons({});
    // statementKey is the stable identity for the selected source rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, statementKey, groups.dataUpdatedAt]);

  const save = useMutation({
    mutationFn: () => api<MatchGroup>(`/api/bank-reconciliations/${recId}/match-groups`, {
      method: "POST",
      body: {
        statement_allocations: statementLines.map((line) => ({
          statement_line_id: line.id,
          allocated_amount: statementDraft[line.id]?.amount,
          role: statementDraft[line.id]?.role ?? "principal",
        })),
        ledger_allocations: (candidates.data ?? [])
          .filter((candidate) => ledgerDraft[candidate.journal_line_id]?.selected)
          .map((candidate) => ({
            journal_line_id: candidate.journal_line_id,
            allocated_amount: ledgerDraft[candidate.journal_line_id].amount,
            role: ledgerDraft[candidate.journal_line_id].role,
          })),
        settlement_reference: reference || null,
        notes: notes || null,
        tolerance: "0.01",
      },
    }),
    onSuccess: onSaved,
    onError: (error: Error) => toast.error(`Match failed: ${error.message}`),
  });

  const reverse = (group: MatchGroup) => {
    const reason = reverseReasons[group.id]?.trim();
    if (!reason) {
      toast.error("Enter a reversal reason first");
      return;
    }
    confirmAction({
      title: "Reverse this match group?",
      message: "Its allocations will be released and affected statement rows will return to partial or unmatched status. The reversal remains in the audit trail.",
      confirmLabel: "Reverse match",
      kind: "danger",
      onConfirm: async () => {
        try {
          await api(`/api/bank-reconciliations/${recId}/match-groups/${group.id}/reverse`, {
            method: "POST",
            body: { reason },
          });
          await qc.invalidateQueries({ queryKey: ["bank-reconciliation", recId] });
          await qc.invalidateQueries({ queryKey: bankRecKeys.detail(recId) });
          toast.success("Match reversed; source capacity released");
        } catch (error) {
          toast.error(`Reverse failed: ${(error as Error).message}`);
        }
      },
    });
  };

  if (!open) return null;

  const availableCandidates = (candidates.data ?? []).filter((candidate) => {
    if (Number(candidate.available_amount) <= 0) return false;
    const haystack = `${candidate.journal_no} ${candidate.journal_description ?? ""} ${candidate.line_memo ?? ""} ${candidate.account_code} ${candidate.account_name}`.toLowerCase();
    return haystack.includes(search.toLowerCase());
  });
  const statementTotal = Object.values(statementDraft).reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const ledgerTotal = Object.values(ledgerDraft).reduce(
    (sum, row) => sum + (row.selected ? Number(row.amount || 0) : 0),
    0,
  );
  const difference = statementTotal - ledgerTotal;
  const selectedLedgerCount = Object.values(ledgerDraft).filter((row) => row.selected).length;
  const statementAllocationsValid = Object.values(statementDraft).every(
    (row) => Number.isFinite(Number(row.amount)) && Math.abs(Number(row.amount)) > 0,
  );
  const ledgerAllocationsValid = Object.values(ledgerDraft).every(
    (row) => !row.selected || (Number.isFinite(Number(row.amount)) && Math.abs(Number(row.amount)) > 0),
  );
  const canSave = statementLines.length > 0 && selectedLedgerCount > 0
    && statementAllocationsValid && ledgerAllocationsValid && !save.isPending;

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/65 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-950 rounded-2xl shadow-2xl w-full max-w-7xl h-[90vh] overflow-hidden flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 dark:border-gray-800 flex items-center gap-4 bg-slate-950 text-white">
          <div className="flex-1">
            <h2 className="font-semibold text-lg">{statementLines.length ? "Build an industrial match" : "Industrial match history"}</h2>
            <p className="text-xs text-slate-300 mt-0.5">Allocate partials, splits, net settlements, fees, refunds, and many-to-many batches. Signed amounts must net to zero difference.</p>
          </div>
          <div className={`rounded-lg px-3 py-1.5 text-right ${Math.abs(difference) <= 0.01 ? "bg-emerald-500/20 text-emerald-200" : "bg-amber-500/20 text-amber-100"}`}>
            <div className="text-[10px] uppercase tracking-wider opacity-75">Difference</div>
            <div className="font-mono font-semibold">{fmtMoney(difference)}</div>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-white/10">✕</button>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[0.85fr_1.35fr]">
          <div className="min-h-0 overflow-y-auto border-r border-slate-200 dark:border-gray-800 p-5">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">1 · Statement allocations</h3>
            <p className="text-xs text-slate-500 mb-3">Use less than the source amount to leave a partial remainder for another group.</p>
            <div className="space-y-2">
              {statementLines.length === 0 && (
                <p className="rounded-lg border border-slate-200 p-3 text-xs text-slate-600 dark:border-gray-800 dark:text-slate-400">
                  History-only view. Close this panel, select one or more unmatched statement rows, then choose Build match to create a group.
                </p>
              )}
              {statementLines.map((line) => (
                <div key={line.id} className="rounded-lg border border-slate-200 dark:border-gray-800 p-3">
                  <div className="flex justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-slate-800 dark:text-slate-200 truncate">{line.description || "Statement line"}</div>
                      <div className="text-[11px] text-slate-500">{line.txn_date} · source {fmtMoney(line.amount)} · available {fmtMoney(statementDraft[line.id]?.amount)}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-[1fr_1fr] gap-2">
                    <input
                      aria-label={`Statement allocation for ${line.description || line.id}`}
                      value={statementDraft[line.id]?.amount ?? ""}
                      onChange={(event) => setStatementDraft((current) => ({ ...current, [line.id]: { ...current[line.id], amount: event.target.value } }))}
                      className="rounded-md border border-slate-300 dark:border-gray-700 bg-transparent px-2 py-1.5 text-sm font-mono"
                    />
                    <select
                      aria-label={`Statement role for ${line.description || line.id}`}
                      value={statementDraft[line.id]?.role ?? "principal"}
                      onChange={(event) => setStatementDraft((current) => ({ ...current, [line.id]: { ...current[line.id], role: event.target.value as MatchRole } }))}
                      className="rounded-md border border-slate-300 dark:border-gray-700 bg-transparent px-2 py-1.5 text-sm"
                    >
                      {MATCH_ROLES.map((role) => <option key={role} value={role}>{role}</option>)}
                    </select>
                  </div>
                </div>
              ))}
            </div>

            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mt-5">2 · Settlement evidence</h3>
            <div className="space-y-2 mt-2">
              <input
                placeholder="Settlement / payout reference (optional)"
                value={reference}
                onChange={(event) => setReference(event.target.value)}
                className="w-full rounded-md border border-slate-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
              />
              <textarea
                placeholder="Reviewer note or explanation"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                rows={3}
                className="w-full rounded-md border border-slate-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
              />
            </div>

            <div className="mt-5 border-t border-slate-200 dark:border-gray-800 pt-4">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Match history</h3>
              {groups.isLoading ? (
                <p className="text-xs text-slate-500 mt-2">Loading audit groups…</p>
              ) : (groups.data ?? []).length === 0 ? (
                <p className="text-xs text-slate-500 mt-2">No advanced groups yet. The first saved group appears here with its allocations and status.</p>
              ) : (
                <div className="space-y-2 mt-2">
                  {(groups.data ?? []).map((group) => (
                    <div key={group.id} className="rounded-lg bg-slate-50 dark:bg-gray-900 p-2.5 text-xs">
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold ${group.status === "balanced" ? "text-emerald-700" : group.status === "draft" ? "text-amber-700" : "text-slate-500"}`}>{group.status}</span>
                        <span>{group.match_type.replaceAll("_", " ")}</span>
                        <span className="ml-auto font-mono">Δ {fmtMoney(group.difference)}</span>
                      </div>
                      <div className="text-slate-500 mt-1">{group.allocations.length} allocations · {new Date(group.matched_at).toLocaleString()}</div>
                      {group.settlement_reference && <div className="text-slate-500 mt-1">Reference {group.settlement_reference}</div>}
                      {group.notes && <div className="text-slate-600 mt-1 whitespace-pre-wrap">{group.notes}</div>}
                      <details className="mt-1.5 text-slate-600">
                        <summary className="cursor-pointer font-medium">Allocation evidence</summary>
                        <div className="mt-1 space-y-0.5 font-mono text-[10px]">
                          {group.allocations.map((allocation) => (
                            <div key={allocation.id}>
                              {allocation.side} · {allocation.role} · {fmtMoney(allocation.allocated_amount)} · source {fmtMoney(allocation.source_amount)}
                            </div>
                          ))}
                        </div>
                      </details>
                      {group.status !== "reversed" && (
                        <div className="mt-2 flex gap-2">
                          <input
                            aria-label={`Reversal reason for ${group.match_type.replaceAll("_", " ")} group`}
                            placeholder="Required reversal reason"
                            value={reverseReasons[group.id] ?? ""}
                            onChange={(event) => setReverseReasons((current) => ({ ...current, [group.id]: event.target.value }))}
                            className="min-w-0 flex-1 rounded border border-slate-300 bg-white px-2 py-1 dark:border-gray-700 dark:bg-gray-950"
                          />
                          <button type="button" onClick={() => reverse(group)} className="text-red-700 hover:underline">Reverse</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="min-h-0 flex flex-col p-5">
            <div className="flex items-start gap-3 mb-3">
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">3 · Posted ledger allocations</h3>
                <p className="text-xs text-slate-500">Cash lines keep their sign. Clearing credits normalize positive; fee-expense debits normalize negative.</p>
              </div>
              <input
                aria-label="Search posted ledger candidates"
                placeholder="Search JE, account, memo…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="w-64 rounded-md border border-slate-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
              />
            </div>
            {candidates.isLoading ? (
              <div className="flex-1 flex items-center justify-center text-sm text-slate-500">Loading posted ledger evidence…</div>
            ) : candidates.error ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                Could not load candidates: {(candidates.error as Error).message}
                <button type="button" onClick={() => candidates.refetch()} className="block mt-2 font-semibold hover:underline">Retry</button>
              </div>
            ) : availableCandidates.length === 0 ? (
              <div className="rounded-lg border border-slate-200 p-5 text-sm text-slate-600">
                No available posted lines match this search or period window. Post the missing settlement/fee journal, or clear the search.
                <Link to="/journals" className="block mt-2 text-blue-700 font-semibold hover:underline">Open journals →</Link>
              </div>
            ) : (
              <div className="flex-1 min-h-0 overflow-y-auto border border-slate-200 dark:border-gray-800 rounded-lg">
                {availableCandidates.map((candidate) => {
                  const draft = ledgerDraft[candidate.journal_line_id];
                  return (
                    <div key={candidate.journal_line_id} className={`grid grid-cols-[28px_1fr_130px_125px] gap-2 items-center px-3 py-2.5 border-b border-slate-100 dark:border-gray-800 ${draft?.selected ? "bg-violet-50 dark:bg-violet-950/20" : ""}`}>
                      <input
                        type="checkbox"
                        aria-label={`Select journal ${candidate.journal_no} ${candidate.account_name}`}
                        checked={draft?.selected ?? false}
                        onChange={(event) => setLedgerDraft((current) => ({
                          ...current,
                          [candidate.journal_line_id]: {
                            selected: event.target.checked,
                            amount: current[candidate.journal_line_id]?.amount ?? candidate.suggested_amount,
                            role: current[candidate.journal_line_id]?.role ?? candidate.suggested_role,
                          },
                        }))}
                      />
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-slate-800 dark:text-slate-200">JE {candidate.journal_no} · {candidate.journal_date}</div>
                        <div className="text-xs text-slate-600 dark:text-slate-400 truncate">{labelOf(candidate.account_code, candidate.account_name)} · {candidate.line_memo || candidate.journal_description || "No memo"}</div>
                        <div className="text-[10px] text-slate-400">Available {fmtMoney(candidate.available_amount)} · suggested {fmtMoney(candidate.suggested_amount)}</div>
                      </div>
                      <input
                        aria-label={`Allocation amount for journal ${candidate.journal_no}`}
                        disabled={!draft?.selected}
                        value={draft?.amount ?? candidate.suggested_amount}
                        onChange={(event) => setLedgerDraft((current) => ({ ...current, [candidate.journal_line_id]: { selected: true, amount: event.target.value, role: current[candidate.journal_line_id]?.role ?? candidate.suggested_role } }))}
                        className="rounded-md border border-slate-300 dark:border-gray-700 bg-transparent px-2 py-1.5 text-sm font-mono disabled:opacity-40"
                      />
                      <select
                        aria-label={`Allocation role for journal ${candidate.journal_no}`}
                        disabled={!draft?.selected}
                        value={draft?.role ?? candidate.suggested_role}
                        onChange={(event) => setLedgerDraft((current) => ({ ...current, [candidate.journal_line_id]: { selected: true, amount: current[candidate.journal_line_id]?.amount ?? candidate.suggested_amount, role: event.target.value as MatchRole } }))}
                        className="rounded-md border border-slate-300 dark:border-gray-700 bg-transparent px-2 py-1.5 text-sm disabled:opacity-40"
                      >
                        {MATCH_ROLES.map((role) => <option key={role} value={role}>{role}</option>)}
                      </select>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 dark:border-gray-800 flex items-center gap-4">
          <div className="text-xs text-slate-500">
            Statement {fmtMoney(statementTotal)} · Ledger {fmtMoney(ledgerTotal)} · {selectedLedgerCount} ledger allocation(s)
          </div>
          <div className="flex-1" />
          <button type="button" onClick={onClose} disabled={save.isPending} className="text-sm px-3 py-2 rounded-md border border-slate-300 hover:bg-slate-50 dark:hover:bg-gray-900">Cancel</button>
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={!canSave}
            className={`text-sm px-4 py-2 rounded-md font-semibold text-white disabled:opacity-40 ${Math.abs(difference) <= 0.01 ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-600 hover:bg-amber-700"}`}
          >
            {save.isPending ? "Saving…" : Math.abs(difference) <= 0.01 ? "Save balanced match" : "Save draft group"}
          </button>
        </div>
      </div>
    </div>
  );
}

const MATCH_ROLES: MatchRole[] = ["principal", "settlement", "fee", "adjustment", "refund", "chargeback"];

// ─── AI Recon Assist dialog ──────────────────────────────────────────────

function AiReconDialog({
  open, onClose, data, isPending, error, onRefresh, recId, onApplied, toast,
}: {
  open: boolean;
  onClose: () => void;
  data: AiReconResult | null;
  isPending: boolean;
  error: string | null;
  onRefresh: () => void;
  recId: string;
  onApplied: () => void;
  toast: ReturnType<typeof useToast>;
}) {
  const [applying, setApplying] = useState<string | null>(null);
  const [bulkApplying, setBulkApplying] = useState(false);

  if (!open) return null;

  const applyOne = async (m: AiReconMatch) => {
    setApplying(m.statement_line_id);
    try {
      await api(`/api/bank-reconciliations/${recId}/manual-match`, {
        method: "POST",
        body: {
          statement_line_id: m.statement_line_id,
          journal_line_id: m.journal_line_id,
        },
      });
      toast.success(`Matched ${fmtMoney(m.amount_cents / 100)}`);
      onApplied();
    } catch (e) {
      toast.error(`Match failed: ${(e as Error).message}`);
    } finally {
      setApplying(null);
    }
  };

  const applyAllHigh = async () => {
    if (!data) return;
    const highMatches = data.matches.filter((m) => m.confidence === "high");
    if (highMatches.length === 0) return;
    setBulkApplying(true);
    let ok = 0;
    let fail = 0;
    // Sequential to keep error messages attributable; the per-call cost is
    // negligible compared to the AI call we just made.
    for (const m of highMatches) {
      try {
        await api(`/api/bank-reconciliations/${recId}/manual-match`, {
          method: "POST",
          body: {
            statement_line_id: m.statement_line_id,
            journal_line_id: m.journal_line_id,
          },
        });
        ok++;
      } catch {
        fail++;
      }
    }
    setBulkApplying(false);
    if (fail > 0) toast.error(`Applied ${ok}/${ok + fail} — ${fail} failed`);
    else toast.success(`Applied ${ok} high-confidence matches`);
    onApplied();
  };

  const confidenceColor = (c: AiReconMatch["confidence"]) =>
    c === "high" ? "bg-emerald-100 text-emerald-700 border-emerald-300"
    : c === "medium" ? "bg-blue-100 text-blue-700 border-blue-300"
    : "bg-slate-100 text-slate-600 border-slate-300";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-3 border-b border-slate-200 dark:border-gray-700 flex items-center gap-3">
          <span className="text-lg">✨</span>
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-slate-900 dark:text-slate-100">AI Reconciliation Assist</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Proposed matches between unmatched bank lines and unmatched JEs.
              Every match validated for exact amount.
            </p>
          </div>
          <button
            type="button"
            onClick={onRefresh}
            disabled={isPending}
            className="text-xs px-2 py-1 rounded border border-slate-200 dark:border-gray-700 hover:bg-slate-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            {isPending ? "Thinking…" : "↻ Re-run"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded hover:bg-slate-100 dark:hover:bg-gray-800 text-slate-500"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 mb-3 text-sm">
              {error}
            </div>
          )}
          {isPending && !data && (
            <div className="text-center py-10 text-slate-500 dark:text-slate-400">
              AI is reviewing your unmatched lines…
            </div>
          )}
          {data && (
            <>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3 mb-4 text-sm text-slate-800 dark:text-slate-200">
                {data.summary}
              </div>

              {/* Summary stats + bulk apply */}
              <div className="flex items-center gap-4 mb-3">
                <div className="text-sm">
                  <span className="font-semibold text-lg">{data.matches_proposed}</span>
                  <span className="text-slate-500 ml-1">proposed</span>
                </div>
                <div className="text-sm">
                  <span className="font-semibold text-lg text-emerald-700">{data.high_confidence_count}</span>
                  <span className="text-slate-500 ml-1">high confidence</span>
                </div>
                <div className="flex-1" />
                {data.high_confidence_count > 0 && (
                  <button
                    type="button"
                    onClick={applyAllHigh}
                    disabled={bulkApplying}
                    className="text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-3 py-1.5 font-medium disabled:opacity-50"
                  >
                    {bulkApplying ? "Applying…" : `✓ Apply all ${data.high_confidence_count} high-confidence`}
                  </button>
                )}
              </div>

              {/* Matches */}
              {data.matches.length === 0 ? (
                <div className="text-sm text-slate-500 py-4 text-center">
                  No matches proposed. See orphans below for what AI thinks is missing.
                </div>
              ) : (
                <div className="space-y-2 mb-5">
                  {data.matches.map((m) => (
                    <div
                      key={m.statement_line_id}
                      className="border border-slate-200 dark:border-gray-700 rounded-md p-3 text-sm"
                    >
                      <div className="flex items-start gap-3">
                        <span className={`text-xs px-2 py-0.5 rounded border ${confidenceColor(m.confidence)}`}>
                          {m.confidence}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 mb-1">
                            <span className="font-mono font-semibold text-base">{fmtMoney(m.amount_cents / 100)}</span>
                            {m.date_diff_days !== null && (
                              <span className="text-xs text-slate-500">
                                {m.date_diff_days === 0 ? "same day" : `${m.date_diff_days}d apart`}
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-xs">
                            <div>
                              <div className="text-slate-500 uppercase tracking-wide mb-0.5">Bank</div>
                              <div className="font-medium">{m.statement_date}</div>
                              <div className="text-slate-600 dark:text-slate-400 truncate">{m.statement_description}</div>
                              {m.statement_reference && (
                                <div className="text-slate-400 font-mono text-[10px]">ref: {m.statement_reference}</div>
                              )}
                            </div>
                            <div>
                              <div className="text-slate-500 uppercase tracking-wide mb-0.5">JE-{m.journal_no}</div>
                              <div className="font-medium">{m.journal_date}</div>
                              <div className="text-slate-600 dark:text-slate-400 truncate">{m.journal_description}</div>
                            </div>
                          </div>
                          <div className="text-xs text-slate-500 italic mt-1">{m.reasoning}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => applyOne(m)}
                          disabled={applying === m.statement_line_id || bulkApplying}
                          className="text-xs bg-emerald-100 hover:bg-emerald-200 text-emerald-800 rounded px-2 py-1 font-medium disabled:opacity-50 dark:bg-emerald-900/20 dark:text-emerald-300"
                        >
                          {applying === m.statement_line_id ? "…" : "Apply"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Orphans */}
              {data.orphan_statement_lines.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                    Bank lines without a JE match
                  </h3>
                  <div className="space-y-1">
                    {data.orphan_statement_lines.map((o) => (
                      <div key={o.id} className="text-sm flex gap-3 items-baseline border-l-2 border-amber-300 pl-3 py-1">
                        <span className="font-mono">{fmtMoney(o.amount_cents / 100)}</span>
                        <span className="text-slate-500 text-xs">{o.date}</span>
                        <span className="flex-1 text-slate-600 dark:text-slate-400 truncate">{o.description}</span>
                        {o.best_guess && (
                          <span className="text-xs text-amber-700 italic">→ {o.best_guess}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {data.orphan_journal_lines.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                    JEs without a bank line (outstanding / in-transit?)
                  </h3>
                  <div className="space-y-1">
                    {data.orphan_journal_lines.map((o) => (
                      <div key={o.id} className="text-sm flex gap-3 items-baseline border-l-2 border-blue-300 pl-3 py-1">
                        <span className="font-mono">{fmtMoney(o.amount_cents / 100)}</span>
                        <span className="text-slate-500 text-xs">JE-{o.journal_no} · {o.date}</span>
                        <span className="flex-1 text-slate-600 dark:text-slate-400 truncate">{o.description}</span>
                        {o.explanation && (
                          <span className="text-xs text-blue-700 italic">{o.explanation}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="text-xs text-slate-400 text-right mt-3">
                Generated {new Date(data.generated_at).toLocaleString()} · {data.model}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
